import { useEffect, useId, useRef, useState } from 'react';

interface HoursInputProps {
  value: number | null;
  /**
   * Commit-Callback. Wird wie bei `MoneyInput` NICHT live gefeuert,
   * sondern bei Blur, Enter oder nach 300 ms Idle. Siehe MoneyInput.tsx
   * für die ausführliche Begründung.
   */
  onChange: (hours: number | null) => void;
  label: string;
  hint?: string;
  placeholder?: string;
  errorMessage?: string;
  /** Read-only-Variante zeigt die Zahl ohne Border (für abgeleitete Werte). */
  readOnly?: boolean;
  /** Optionale Suffix-Beschriftung (default: „h / Monat"). */
  suffix?: string;
  inputId?: string;
  /** Debounce-Delay in ms. Default 300. */
  debounceMs?: number;
}

/**
 * Editorial-Hours-Input mit deutschem Locale.
 *
 * Wert ist eine Number (kann float sein, z.B. 87.5). Im Display nutzen wir
 * Komma als Dezimaltrenner. Source-Serif-Black, tabular-nums.
 *
 * Commit-Modell: identisch zu MoneyInput — Daten werden bei Blur, Enter
 * oder nach 300 ms Idle committet, NICHT pro Tastendruck. Damit verhalten
 * sich beide Eingaben für die Score-Engine konsistent.
 */
export function HoursInput({
  value,
  onChange,
  label,
  hint,
  placeholder,
  errorMessage,
  readOnly = false,
  suffix = 'h / Monat',
  inputId,
  debounceMs = 300,
}: HoursInputProps) {
  const generatedId = useId();
  const id = inputId ?? generatedId;
  const [draft, setDraft] = useState<string>(() => formatHoursForEdit(value));
  const [hasFocus, setHasFocus] = useState(false);

  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedRef = useRef<number | null>(value);

  useEffect(() => {
    if (!hasFocus) {
      setDraft(formatHoursForEdit(value));
      lastCommittedRef.current = value;
    }
  }, [value, hasFocus]);

  useEffect(() => {
    return () => {
      if (commitTimer.current !== null) clearTimeout(commitTimer.current);
    };
  }, []);

  const commit = (raw: string) => {
    if (commitTimer.current !== null) {
      clearTimeout(commitTimer.current);
      commitTimer.current = null;
    }
    const parsed = parseHours(raw);
    if (parsed !== lastCommittedRef.current) {
      lastCommittedRef.current = parsed;
      onChange(parsed);
    }
  };

  const scheduleDebouncedCommit = (raw: string) => {
    if (commitTimer.current !== null) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => {
      commitTimer.current = null;
      commit(raw);
    }, debounceMs);
  };

  const display = hasFocus ? draft : formatHoursForDisplay(value);

  if (readOnly) {
    return (
      <div className="block">
        <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {label}
        </span>
        {hint && (
          <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">{hint}</p>
        )}
        <div className="mt-3 flex items-baseline gap-2">
          <span
            id={id}
            className="font-serif text-3xl font-black tabular-nums tracking-tight text-stone-900 md:text-4xl"
          >
            {value === null ? '—' : formatHoursForDisplay(value)}
          </span>
          {value !== null && (
            <span aria-hidden="true" className="font-serif text-xl text-stone-500">
              {suffix}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="block">
      <label htmlFor={id}>
        <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {label}
        </span>
        {hint && (
          <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">{hint}</p>
        )}
      </label>
      <div
        className={`mt-3 flex items-baseline gap-2 border-b transition-colors ${
          errorMessage ? 'border-rose-500' : hasFocus ? 'border-stone-900' : 'border-stone-300'
        }`}
      >
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={display}
          placeholder={placeholder ?? '0'}
          onFocus={() => {
            setHasFocus(true);
            setDraft(formatHoursForEdit(value));
          }}
          onBlur={() => {
            setHasFocus(false);
            commit(draft);
            setDraft(formatHoursForEdit(parseHours(draft)));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit(draft);
            }
          }}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            scheduleDebouncedCommit(next);
          }}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={errorMessage ? `${id}-err` : undefined}
          className="w-full bg-transparent py-2 font-serif text-3xl font-black tabular-nums tracking-tight text-stone-900 outline-none placeholder:text-stone-300 md:text-4xl"
        />
        <span aria-hidden="true" className="whitespace-nowrap font-serif text-xl text-stone-500">
          {suffix}
        </span>
      </div>
      {errorMessage && (
        <p id={`${id}-err`} className="mt-2 font-sans text-xs font-medium text-rose-600">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

export function parseHours(input: string): number | null {
  const trimmed = input.trim().replace(/\s/g, '');
  if (trimmed === '') return null;
  // Komma → Punkt für Number()
  const normalized = trimmed.replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const n = Number(normalized);
  if (Number.isNaN(n) || n < 0) return null;
  return n;
}

export function formatHoursForDisplay(hours: number | null): string {
  if (hours === null) return '';
  return hours.toLocaleString('de-DE', {
    minimumFractionDigits: hours % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 2,
  });
}

export function formatHoursForEdit(hours: number | null): string {
  if (hours === null) return '';
  // Ohne Tausender, mit Komma als Dezimal — leicht zu editieren
  return hours
    .toLocaleString('de-DE', {
      minimumFractionDigits: hours % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 2,
      useGrouping: false,
    })
    .replace(/\./g, '');
}
